import React, { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { 
  Calculator, Brain, Heart, Activity, Users, Utensils, Bone, FlaskConical, 
  AlertTriangle, ClipboardList, Info, Check, ChevronDown, ChevronUp, Euro, 
  Wind, Thermometer, Baby, Stethoscope, Search, Star, Sparkles, MessageCircle,
  Send, Loader2, Target, Pill, ClipboardCheck, RefreshCw, User
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { VoiceInput } from '../components/VoiceInput';
import { SCORES_DATABASE, getAllScores, searchScores } from '../data/scoresDatabase';
import { useFavorites } from '../contexts/FavoritesContext';
import { PageHeader } from '../components/PageHeader';

const BACKEND_URL = '';
const API = `${BACKEND_URL}/api`;

// Mapping des noms d'icônes vers les composants Lucide
const ICON_MAP = {
  Heart: Heart,
  Wind: Wind,
  AlertTriangle: AlertTriangle,
  Brain: Brain,
  Users: Users,
  Activity: Activity,
  FlaskConical: FlaskConical,
  Utensils: Utensils,
  Bone: Bone,
  Thermometer: Thermometer,
  Baby: Baby,
  Stethoscope: Stethoscope,
  Calculator: Calculator
};

// Helper pour obtenir le composant icône
const getIconComponent = (iconName) => {
  return ICON_MAP[iconName] || Activity;
};

// ============================================
// COMPOSANT PRINCIPAL
// ============================================

export const ScoresPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedScore, setSelectedScore] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const { favorites, toggleFavorite, isFavorite, remainingSlots } = useFavorites();
  
  // AI Treatment Recommendations state
  const [treatmentRecommendation, setTreatmentRecommendation] = useState(null);
  const [loadingRecommendation, setLoadingRecommendation] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [loadingChat, setLoadingChat] = useState(false);
  const [patientContext, setPatientContext] = useState('');

  // Gérer l'ouverture automatique d'un score via URL (ex: /scores?score=chadsvasc)
  useEffect(() => {
    const scoreIdFromUrl = searchParams.get('score');
    if (scoreIdFromUrl) {
      // Chercher le score dans toutes les catégories
      for (const [categoryKey, category] of Object.entries(SCORES_DATABASE)) {
        const foundScore = category.scores.find(s => 
          s.id === scoreIdFromUrl || 
          s.id.toLowerCase() === scoreIdFromUrl.toLowerCase()
        );
        if (foundScore) {
          setSelectedScore(foundScore);
          setSelectedCategory(categoryKey);
          setAnswers({});
          setResult(null);
          toast.success(`Score ${foundScore.name} ouvert`);
          // Clear the URL parameter
          setSearchParams({});
          break;
        }
      }
    }
  }, [searchParams, setSearchParams]);

  // Calculer le nombre total de scores
  const totalScores = useMemo(() => {
    return Object.values(SCORES_DATABASE).reduce((sum, cat) => sum + cat.scores.length, 0);
  }, []);

  // Utiliser les helpers de la base de données
  const searchResults = useMemo(() => {
    if (!searchTerm.trim()) return null;
    return searchScores(searchTerm);
  }, [searchTerm]);

  const handleSelectScore = (score, categoryKey) => {
    setSelectedScore(score);
    setSelectedCategory(categoryKey);
    setAnswers({});
    setResult(null);
    setTreatmentRecommendation(null);
    setChatMessages([]);
    setShowChat(false);
    setPatientContext('');
  };

  const handleToggleFavorite = (score, categoryKey, categoryName, e) => {
    e.stopPropagation(); // Prevent triggering score selection
    const scoreWithCategory = {
      ...score,
      categoryKey,
      categoryName
    };
    const result = toggleFavorite(scoreWithCategory);
    if (result.success) {
      toast.success(result.message);
    } else {
      toast.error(result.message);
    }
  };

  const calculateScore = () => {
    if (!selectedScore) return;

    let totalScore = 0;
    let subscores = {};

    // Différents types de calculs selon le score
    if (selectedScore.questions) {
      // Score avec questions (PHQ-9, HAD, etc.)
      if (selectedScore.subscales) {
        // Score avec sous-échelles (HAD)
        selectedScore.subscales.forEach(sub => {
          subscores[sub] = 0;
        });
        selectedScore.questions.forEach((q, idx) => {
          const answer = answers[`q${idx}`];
          if (answer !== undefined) {
            if (q.subscale) {
              subscores[q.subscale] += answer;
            }
            totalScore += answer;
          }
        });
      } else {
        // Score simple
        selectedScore.questions.forEach((q, idx) => {
          const answer = answers[`q${idx}`];
          if (answer !== undefined) {
            totalScore += typeof answer === 'boolean' ? (answer ? 1 : 0) : answer;
          }
        });
      }
    } else if (selectedScore.hamiltonQuestions) {
      // Score Hamilton (17 items avec options individuelles)
      selectedScore.hamiltonQuestions.forEach((q) => {
        const answer = answers[q.id];
        if (answer !== undefined) {
          totalScore += answer;
        }
      });
    } else if (selectedScore.beckQuestions) {
      // Score Beck BDI-II (21 items auto-questionnaire)
      selectedScore.beckQuestions.forEach((q) => {
        const answer = answers[q.id];
        if (answer !== undefined) {
          totalScore += answer;
        }
      });
    } else if (selectedScore.miniModules) {
      // MINI - Calculer les résultats de chaque module
      const moduleResults = {};
      selectedScore.miniModules.forEach((module) => {
        const moduleAnswers = {};
        module.questions.forEach(q => {
          if (answers[q.id] !== undefined) moduleAnswers[q.id] = answers[q.id];
        });
        moduleResults[module.id] = module.interpretation(moduleAnswers);
      });
      // Le score total pour MINI = nombre de modules positifs
      totalScore = Object.values(moduleResults).filter(r => r.positive).length;
      subscores = moduleResults;
    } else if (selectedScore.cageQuestions) {
      // Score CAGE (4 questions OUI/NON)
      selectedScore.cageQuestions.forEach((q) => {
        if (answers[q.id] === true) {
          totalScore += 1;
        }
      });
    } else if (selectedScore.fields) {
      // Score avec champs (SCORE2, CHA2DS2-VASc, etc.)
      if (selectedScore.calculate) {
        // Fonction de calcul personnalisée
        totalScore = selectedScore.calculate(answers);
      } else {
        // Somme des points
        selectedScore.fields.forEach(field => {
          if (field.points && answers[field.id]) {
            totalScore += field.points;
          }
        });
      }
    } else if (selectedScore.sections) {
      // Score avec sections (MMSE, Glasgow)
      Object.values(answers).forEach(val => {
        if (typeof val === 'number') totalScore += val;
      });
    } else if (selectedScore.activities) {
      // ADL
      selectedScore.activities.forEach((act, idx) => {
        if (answers[`act${idx}`] !== undefined) {
          totalScore += answers[`act${idx}`];
        }
      });
    } else if (selectedScore.options) {
      // Score à option unique (mMRC)
      totalScore = answers.score || 0;
    }

    // Interprétation
    let interpretation;
    if (selectedScore.miniModules && Object.keys(subscores).length > 0) {
      // MINI - utiliser les résultats des modules pour l'interprétation globale
      interpretation = selectedScore.interpretation(subscores);
    } else if (selectedScore.subscales && Object.keys(subscores).length > 0) {
      interpretation = selectedScore.interpretation(subscores);
    } else if (selectedScore.id === 'score2') {
      interpretation = selectedScore.interpretation(totalScore, answers.age);
    } else if (selectedScore.id === 'chadsvasc') {
      interpretation = selectedScore.interpretation(totalScore, answers.femme ? 'femme' : 'homme');
    } else {
      interpretation = selectedScore.interpretation(totalScore);
    }

    setResult({
      score: totalScore,
      subscores,
      interpretation,
      cotation: selectedScore.cotation
    });

    toast.success(`Score calculé: ${totalScore}`);
    
    // Reset previous recommendations when calculating new score
    setTreatmentRecommendation(null);
    setChatMessages([]);
  };
  
  // Fetch AI treatment recommendations
  const fetchTreatmentRecommendation = async () => {
    if (!result || !selectedScore) return;
    
    setLoadingRecommendation(true);
    try {
      const response = await axios.post(`${API}/scores/treatment-recommendation`, {
        score_id: selectedScore.id,
        score_name: selectedScore.name,
        score_value: result.score,
        interpretation: typeof result.interpretation === 'object' 
          ? JSON.stringify(result.interpretation) 
          : result.interpretation,
        inputs: answers,
        patient_context: patientContext || null
      });
      
      setTreatmentRecommendation(response.data.recommendation);
      toast.success('Recommandations générées');
    } catch (error) {
      toast.error('Erreur lors de la génération des recommandations');
    } finally {
      setLoadingRecommendation(false);
    }
  };
  
  // Send chat message
  const sendChatMessage = async () => {
    if (!chatInput.trim() || !result || !selectedScore) return;
    
    const userMessage = chatInput.trim();
    setChatInput('');
    setChatMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoadingChat(true);
    
    try {
      const response = await axios.post(`${API}/scores/chat`, {
        score_id: selectedScore.id,
        score_name: selectedScore.name,
        score_value: result.score,
        interpretation: typeof result.interpretation === 'object' 
          ? JSON.stringify(result.interpretation) 
          : result.interpretation,
        inputs: answers,
        message: userMessage,
        conversation_history: chatMessages
      });
      
      setChatMessages(prev => [...prev, { role: 'assistant', content: response.data.response }]);
    } catch (error) {
      toast.error('Erreur lors de l\'envoi du message');
      setChatMessages(prev => [...prev, { role: 'assistant', content: 'Désolé, une erreur est survenue. Veuillez réessayer.' }]);
    } finally {
      setLoadingChat(false);
    }
  };

  const handleVoiceSearch = (text) => {
    setSearchTerm(text);
  };

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="scores-page">
      {/* Header */}
      <PageHeader icon={Calculator} title="Scores Médicaux" subtitle={`${totalScores} calculateurs cliniques avec cotation CCAM`} gradient="from-indigo-600 to-purple-600" testId="scores-header" />

      {/* Search Bar */}
      <div className="glassmorphic rounded-2xl p-4">
        <div className="flex gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Rechercher un score (PHQ-9, CHA2DS2-VASc, MMSE, IMC...)"
              className="w-full pl-12 pr-4 py-3 bg-slate-900/60 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
              data-testid="score-search-input"
            />
          </div>
          <VoiceInput 
            onTranscription={handleVoiceSearch}
            placeholder="Parler"
            className="flex-shrink-0"
          />
        </div>

        {/* Search Results */}
        {searchResults && searchResults.length > 0 && (
          <div className="mt-4 space-y-2">
            <p className="text-sm text-slate-400">{searchResults.length} score(s) trouvé(s)</p>
            <div className="grid gap-2">
              {searchResults.map((score) => (
                <div
                  key={score.id}
                  className="flex items-center justify-between p-3 bg-slate-800/50 rounded-xl hover:bg-slate-800 transition-colors"
                >
                  <button
                    onClick={() => handleSelectScore(score, score.categoryKey)}
                    className="flex-1 text-left"
                  >
                    <span className="text-white font-medium">{score.name}</span>
                    <span className="text-slate-400 text-sm ml-2">- {score.fullName}</span>
                    <p className="text-slate-500 text-xs">{score.description}</p>
                  </button>
                  <div className="flex items-center gap-2 ml-3">
                    <button
                      onClick={(e) => handleToggleFavorite(score, score.categoryKey, score.categoryName, e)}
                      className={`p-2 rounded-lg transition-colors ${
                        isFavorite(score.id)
                          ? 'text-amber-400 bg-amber-500/20'
                          : 'text-slate-500 hover:text-amber-400 hover:bg-slate-700'
                      }`}
                      data-testid={`search-favorite-btn-${score.id}`}
                    >
                      <Star className={`w-4 h-4 ${isFavorite(score.id) ? 'fill-current' : ''}`} />
                    </button>
                    <span className="text-xs text-indigo-400 bg-indigo-500/20 px-2 py-1 rounded">{score.categoryName}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Categories Grid */}
      {!selectedScore && !searchTerm && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {Object.entries(SCORES_DATABASE).map(([key, category]) => {
            const Icon = getIconComponent(category.icon);
            return (
              <button
                key={key}
                onClick={() => setSelectedCategory(selectedCategory === key ? null : key)}
                className={`glassmorphic rounded-2xl p-4 text-left transition-all hover:scale-105 ${selectedCategory === key ? 'ring-2 ring-indigo-500' : ''}`}
                data-testid={`category-${key}`}
              >
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${category.color} flex items-center justify-center mb-3`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-white font-semibold">{category.name}</h3>
                <p className="text-slate-400 text-sm">{category.scores.length} scores</p>
              </button>
            );
          })}
        </div>
      )}

      {/* Scores List for Selected Category */}
      {selectedCategory && !selectedScore && !searchTerm && (
        <div className="glassmorphic rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-white flex items-center gap-2">
              {React.createElement(getIconComponent(SCORES_DATABASE[selectedCategory].icon), { className: "w-6 h-6" })}
              {SCORES_DATABASE[selectedCategory].name}
            </h2>
            <button
              onClick={() => setSelectedCategory(null)}
              className="text-slate-400 hover:text-white text-sm"
            >
              ← Retour aux catégories
            </button>
          </div>
          <div className="grid gap-3">
            {SCORES_DATABASE[selectedCategory].scores.map((score) => (
              <div
                key={score.id}
                className="flex items-center justify-between p-4 bg-slate-800/50 rounded-xl hover:bg-slate-800 transition-colors"
                data-testid={`score-${score.id}`}
              >
                <button
                  onClick={() => handleSelectScore(score, selectedCategory)}
                  className="flex-1 text-left"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-white font-semibold">{score.name}</span>
                    {score.cotation && (
                      <span className="text-xs text-green-400 bg-green-500/20 px-2 py-0.5 rounded flex items-center gap-1">
                        <Euro className="w-3 h-3" />
                        {score.cotation.tarif}€
                      </span>
                    )}
                  </div>
                  <p className="text-slate-400 text-sm">{score.fullName}</p>
                  <p className="text-slate-500 text-xs mt-1">{score.description}</p>
                </button>
                <div className="flex items-center gap-2 ml-3">
                  <button
                    onClick={(e) => handleToggleFavorite(score, selectedCategory, SCORES_DATABASE[selectedCategory].name, e)}
                    className={`p-2 rounded-lg transition-colors ${
                      isFavorite(score.id)
                        ? 'text-amber-400 bg-amber-500/20 hover:bg-amber-500/30'
                        : 'text-slate-500 hover:text-amber-400 hover:bg-slate-700'
                    }`}
                    title={isFavorite(score.id) ? 'Retirer des favoris' : 'Ajouter aux favoris'}
                    data-testid={`favorite-btn-${score.id}`}
                  >
                    <Star className={`w-5 h-5 ${isFavorite(score.id) ? 'fill-current' : ''}`} />
                  </button>
                  <ChevronDown className="w-5 h-5 text-slate-400" />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Score Calculator */}
      {selectedScore && (
        <div className="glassmorphic rounded-2xl p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-white">{selectedScore.name}</h2>
              <p className="text-slate-400">{selectedScore.fullName}</p>
              <p className="text-slate-500 text-sm mt-1">{selectedScore.description}</p>
            </div>
            <button
              onClick={() => {
                setSelectedScore(null);
                setResult(null);
                setAnswers({});
              }}
              className="text-slate-400 hover:text-white px-4 py-2 rounded-lg bg-slate-800"
            >
              ← Retour
            </button>
          </div>

          {/* Cotation Info */}
          {selectedScore.cotation && (
            <div className="mb-6 p-4 bg-green-900/20 border border-green-500/30 rounded-xl">
              <div className="flex items-center gap-2 text-green-400 font-medium">
                <Euro className="w-5 h-5" />
                Cotation associée
              </div>
              <p className="text-white mt-1">
                <span className="font-mono text-green-400">{selectedScore.cotation.code}</span> - {selectedScore.cotation.libelle}
              </p>
              <p className="text-emerald-400 font-semibold text-lg">{selectedScore.cotation.tarif}€</p>
              {selectedScore.cotation.note && (
                <p className="text-slate-400 text-sm mt-1">{selectedScore.cotation.note}</p>
              )}
            </div>
          )}

          {/* Questions / Fields */}
          <div className="space-y-4 mb-6">
            {/* Standard Questions */}
            {selectedScore.questions && !selectedScore.subscales && (
              selectedScore.questions.map((q, idx) => (
                <div key={`q-${idx}-${typeof q === 'string' ? q.slice(0, 20) : q.text?.slice(0, 20)}`} className="p-4 bg-slate-800/50 rounded-xl">
                  <p className="text-white mb-3">{typeof q === 'string' ? q : q.text}</p>
                  <div className="flex flex-wrap gap-2">
                    {(selectedScore.options || q.options || [{ label: "Oui", value: 1 }, { label: "Non", value: 0 }]).map((opt) => (
                      <button
                        key={`opt-${idx}-${opt.value}-${opt.label}`}
                        onClick={() => setAnswers({ ...answers, [`q${idx}`]: opt.value })}
                        className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                          answers[`q${idx}`] === opt.value
                            ? 'bg-indigo-600 text-white'
                            : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              ))
            )}

            {/* HAD-style questions with subscales */}
            {selectedScore.questions && selectedScore.subscales && (
              selectedScore.questions.map((q, idx) => (
                <div key={`had-${idx}-${q.subscale}`} className="p-4 bg-slate-800/50 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`text-xs px-2 py-0.5 rounded ${q.subscale === 'anxiete' ? 'bg-purple-500/30 text-purple-300' : 'bg-blue-500/30 text-blue-300'}`}>
                      {q.subscale === 'anxiete' ? 'Anxiété' : 'Dépression'}
                    </span>
                  </div>
                  <p className="text-white mb-3">{q.text}</p>
                  <div className="flex flex-wrap gap-2">
                    {q.options.map((opt) => (
                      <button
                        key={`had-opt-${idx}-${opt.value}`}
                        onClick={() => setAnswers({ ...answers, [`q${idx}`]: opt.value })}
                        className={`px-3 py-2 rounded-lg text-sm transition-colors ${
                          answers[`q${idx}`] === opt.value
                            ? 'bg-indigo-600 text-white'
                            : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              ))
            )}

            {/* Hamilton-style questions (17 items with individual options) */}
            {selectedScore.hamiltonQuestions && (
              selectedScore.hamiltonQuestions.map((q, idx) => (
                <div key={q.id} className="p-4 bg-slate-800/50 rounded-xl">
                  <div className="mb-2">
                    <p className="text-white font-medium">{q.label}</p>
                    {q.description && (
                      <p className="text-slate-400 text-sm mt-1">{q.description}</p>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2 mt-3">
                    {q.options.map((opt) => (
                      <button
                        key={`${q.id}-opt-${opt.value}`}
                        onClick={() => setAnswers({ ...answers, [q.id]: opt.value })}
                        className={`px-3 py-2 rounded-lg text-sm transition-colors ${
                          answers[q.id] === opt.value
                            ? 'bg-purple-600 text-white'
                            : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                        }`}
                      >
                        {opt.label} ({opt.value})
                      </button>
                    ))}
                  </div>
                </div>
              ))
            )}

            {/* Beck BDI-II questions (21 items auto-questionnaire) */}
            {selectedScore.beckQuestions && (
              selectedScore.beckQuestions.map((q, idx) => (
                <div key={q.id} className="p-4 bg-slate-800/50 rounded-xl">
                  <p className="text-white font-medium mb-3">{q.label}</p>
                  <div className="space-y-2">
                    {q.options.map((opt) => (
                      <button
                        key={`beck-${q.id}-${opt.value}`}
                        onClick={() => setAnswers({ ...answers, [q.id]: opt.value })}
                        className={`w-full text-left px-4 py-3 rounded-lg text-sm transition-colors ${
                          answers[q.id] === opt.value
                            ? 'bg-blue-600 text-white border-2 border-blue-400'
                            : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                        }`}
                      >
                        <span className="font-mono text-xs mr-2 opacity-60">{opt.value}.</span>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              ))
            )}

            {/* MINI modules (structured psychiatric interview) */}
            {selectedScore.miniModules && (
              selectedScore.miniModules.map((module, mIdx) => (
                <div key={module.id} className="p-5 bg-slate-800/50 rounded-xl border border-slate-700">
                  <div className="mb-4">
                    <h4 className="text-lg font-semibold text-white">{module.name}</h4>
                    <p className="text-slate-400 text-sm mt-1">{module.description}</p>
                    <p className="text-xs text-indigo-400 mt-2 font-mono">{module.scoring}</p>
                  </div>
                  <div className="space-y-4">
                    {module.questions.map((q, qIdx) => (
                      <div key={q.id} className={`p-3 rounded-lg ${q.required ? 'bg-slate-900/50 border-l-4 border-indigo-500' : 'bg-slate-900/30'}`}>
                        <p className="text-white text-sm mb-3">
                          <span className="font-mono text-indigo-400 mr-2">{q.id}</span>
                          {q.text}
                          {q.required && <span className="text-red-400 ml-1">*</span>}
                        </p>
                        <div className="flex gap-3">
                          <button
                            onClick={() => setAnswers({ ...answers, [q.id]: true })}
                            className={`px-5 py-2 rounded-lg text-sm font-medium transition-colors ${
                              answers[q.id] === true
                                ? 'bg-green-600 text-white'
                                : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                            }`}
                          >
                            OUI
                          </button>
                          <button
                            onClick={() => setAnswers({ ...answers, [q.id]: false })}
                            className={`px-5 py-2 rounded-lg text-sm font-medium transition-colors ${
                              answers[q.id] === false
                                ? 'bg-red-600 text-white'
                                : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                            }`}
                          >
                            NON
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  {/* Module interpretation preview */}
                  {module.interpretation && (
                    <div className="mt-4 p-3 bg-slate-900/50 rounded-lg">
                      {(() => {
                        const moduleAnswers = {};
                        module.questions.forEach(q => {
                          if (answers[q.id] !== undefined) moduleAnswers[q.id] = answers[q.id];
                        });
                        const result = module.interpretation(moduleAnswers);
                        return (
                          <p className={`text-sm font-medium ${result.color}`}>
                            → {result.text}
                          </p>
                        );
                      })()}
                    </div>
                  )}
                </div>
              ))
            )}

            {/* CAGE questions (4 yes/no questions with letters) */}
            {selectedScore.cageQuestions && (
              selectedScore.cageQuestions.map((q) => (
                <div key={q.id} className="p-4 bg-slate-800/50 rounded-xl border-l-4 border-amber-500">
                  <div className="flex items-start gap-3">
                    <span className="text-2xl font-bold text-amber-400 bg-amber-900/30 px-3 py-1 rounded-lg">{q.letter}</span>
                    <div className="flex-1">
                      <p className="text-white font-medium mb-1">{q.label}</p>
                      <p className="text-slate-300 text-sm mb-3">{q.text}</p>
                      <div className="flex gap-3">
                        <button
                          onClick={() => setAnswers({ ...answers, [q.id]: true })}
                          className={`px-6 py-2 rounded-lg text-sm font-medium transition-colors ${
                            answers[q.id] === true
                              ? 'bg-red-600 text-white'
                              : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                          }`}
                        >
                          OUI
                        </button>
                        <button
                          onClick={() => setAnswers({ ...answers, [q.id]: false })}
                          className={`px-6 py-2 rounded-lg text-sm font-medium transition-colors ${
                            answers[q.id] === false
                              ? 'bg-green-600 text-white'
                              : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                          }`}
                        >
                          NON
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}

            {/* Fields (SCORE2, etc.) */}
            {selectedScore.fields && (
              selectedScore.fields.map((field) => (
                <div key={field.id} className="p-4 bg-slate-800/50 rounded-xl">
                  <label className="text-white block mb-2">{field.label}</label>
                  {field.type === 'number' && (
                    <input
                      type="number"
                      min={field.min}
                      max={field.max}
                      step={field.step || 1}
                      value={answers[field.id] || ''}
                      onChange={(e) => setAnswers({ ...answers, [field.id]: parseFloat(e.target.value) })}
                      className="w-full px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white"
                    />
                  )}
                  {field.type === 'boolean' && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => setAnswers({ ...answers, [field.id]: true })}
                        className={`px-4 py-2 rounded-lg ${answers[field.id] === true ? 'bg-indigo-600 text-white' : 'bg-slate-700 text-slate-300'}`}
                      >
                        Oui
                      </button>
                      <button
                        onClick={() => setAnswers({ ...answers, [field.id]: false })}
                        className={`px-4 py-2 rounded-lg ${answers[field.id] === false ? 'bg-indigo-600 text-white' : 'bg-slate-700 text-slate-300'}`}
                      >
                        Non
                      </button>
                    </div>
                  )}
                  {field.type === 'select' && (
                    <select
                      value={answers[field.id] || ''}
                      onChange={(e) => setAnswers({ ...answers, [field.id]: e.target.value })}
                      className="w-full px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white"
                    >
                      <option value="">Sélectionner...</option>
                      {field.options.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  )}
                  {field.type === 'range' && (
                    <div>
                      <input
                        type="range"
                        min={field.min}
                        max={field.max}
                        value={answers[field.id] || field.min}
                        onChange={(e) => setAnswers({ ...answers, [field.id]: parseInt(e.target.value) })}
                        className="w-full"
                      />
                      <div className="flex justify-between text-slate-400 text-sm mt-1">
                        <span>{field.min}</span>
                        <span className="text-2xl font-bold text-white">{answers[field.id] || field.min}</span>
                        <span>{field.max}</span>
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}

            {/* Sections (MMSE, Glasgow) */}
            {selectedScore.sections && (
              selectedScore.sections.map((section, sIdx) => (
                <div key={sIdx} className="p-4 bg-slate-800/50 rounded-xl">
                  <h4 className="text-white font-medium mb-3">{section.name} {section.max && `(/${section.max})`}</h4>
                  {section.options ? (
                    <div className="flex flex-wrap gap-2">
                      {section.options.map((opt) => (
                        <button
                          key={opt.value}
                          onClick={() => setAnswers({ ...answers, [`s${sIdx}`]: opt.value })}
                          className={`px-3 py-2 rounded-lg text-sm ${
                            answers[`s${sIdx}`] === opt.value ? 'bg-indigo-600 text-white' : 'bg-slate-700 text-slate-300'
                          }`}
                        >
                          {opt.label} ({opt.value})
                        </button>
                      ))}
                    </div>
                  ) : (
                    <input
                      type="number"
                      min={0}
                      max={section.max}
                      value={answers[`s${sIdx}`] || ''}
                      onChange={(e) => setAnswers({ ...answers, [`s${sIdx}`]: parseInt(e.target.value) })}
                      className="w-20 px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white"
                      placeholder={`/${section.max}`}
                    />
                  )}
                  {section.items && (
                    <p className="text-slate-500 text-xs mt-2">{section.items.join(', ')}</p>
                  )}
                </div>
              ))
            )}

            {/* Activities (ADL) */}
            {selectedScore.activities && (
              selectedScore.activities.map((act, idx) => (
                <div key={act.id || act.name} className="p-4 bg-slate-800/50 rounded-xl">
                  <p className="text-white mb-3">{act.name}</p>
                  <div className="flex gap-2">
                    {act.options.map((opt) => (
                      <button
                        key={opt.value}
                        onClick={() => setAnswers({ ...answers, [`act${idx}`]: opt.value })}
                        className={`px-4 py-2 rounded-lg text-sm ${
                          answers[`act${idx}`] === opt.value ? 'bg-indigo-600 text-white' : 'bg-slate-700 text-slate-300'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              ))
            )}

            {/* Single option scores (mMRC) */}
            {selectedScore.options && !selectedScore.questions && (
              <div className="space-y-2">
                {selectedScore.options.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setAnswers({ ...answers, score: opt.value })}
                    className={`w-full p-4 rounded-xl text-left transition-colors ${
                      answers.score === opt.value ? 'bg-indigo-600 text-white' : 'bg-slate-800/50 text-slate-300 hover:bg-slate-700'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Calculate Button */}
          <button
            onClick={calculateScore}
            className="w-full py-4 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white font-semibold text-lg transition-all shadow-lg"
            data-testid="calculate-score-btn"
          >
            <Calculator className="inline w-5 h-5 mr-2" />
            Calculer le score
          </button>

          {/* Result */}
          {result && (
            <div className="mt-6 p-6 bg-slate-900/80 rounded-2xl border border-slate-700">
              <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <Check className="w-6 h-6 text-green-400" />
                Résultat
              </h3>
              
              <div className="text-center mb-6">
                <div className="text-5xl font-bold text-white mb-2">
                  {typeof result.score === 'number' ? result.score : JSON.stringify(result.score)}
                </div>
                {result.subscores && Object.keys(result.subscores).length > 0 && !selectedScore?.miniModules && (
                  <div className="flex justify-center gap-4 text-sm">
                    {Object.entries(result.subscores).map(([key, val]) => (
                      <span key={key} className="text-slate-400">
                        {key}: <span className="text-white font-semibold">{typeof val === 'object' ? val.text : val}</span>
                      </span>
                    ))}
                  </div>
                )}
                {/* MINI Module Results */}
                {selectedScore?.miniModules && result.subscores && Object.keys(result.subscores).length > 0 && (
                  <div className="mt-4 text-left space-y-2">
                    <p className="text-slate-400 text-sm font-medium mb-2">Résultats par module :</p>
                    {Object.entries(result.subscores).map(([moduleId, moduleResult]) => (
                      <div key={moduleId} className={`p-2 rounded-lg border ${moduleResult.positive ? 'border-red-500/50 bg-red-900/20' : 'border-green-500/50 bg-green-900/20'}`}>
                        <span className={`text-sm font-medium ${moduleResult.color || (moduleResult.positive ? 'text-red-400' : 'text-green-400')}`}>
                          {moduleId.replace('_', ' - ')}: {moduleResult.text}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Interpretation */}
              {result.interpretation && (
                <div className="space-y-3">
                  {result.interpretation.anxiete && result.interpretation.depression ? (
                    <>
                      <p className={result.interpretation.anxiete.color}>{result.interpretation.anxiete.text}</p>
                      <p className={result.interpretation.depression.color}>{result.interpretation.depression.text}</p>
                      <p className="text-slate-300">→ {result.interpretation.action}</p>
                    </>
                  ) : (
                    <>
                      <p className={`text-xl font-semibold ${result.interpretation.color}`}>
                        {result.interpretation.text}
                      </p>
                      <p className="text-slate-300 bg-slate-800 p-3 rounded-lg">
                        <span className="text-indigo-400 font-medium">Conduite à tenir:</span> {result.interpretation.action}
                      </p>
                    </>
                  )}
                </div>
              )}

              {/* Cotation Reminder */}
              {result.cotation && (
                <div className="mt-4 p-4 bg-green-900/30 border border-green-500/30 rounded-xl">
                  <p className="text-green-400 font-medium flex items-center gap-2">
                    <Euro className="w-4 h-4" />
                    N'oubliez pas de coter: {result.cotation.code} ({result.cotation.tarif}€)
                  </p>
                </div>
              )}
              
              {/* AI Treatment Recommendations Section */}
              <div className="mt-6 space-y-4">
                {/* Patient Context Input */}
                <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-700">
                  <label className="text-sm font-medium text-slate-300 flex items-center gap-2 mb-2">
                    <User className="w-4 h-4" />
                    Contexte patient (optionnel)
                  </label>
                  <textarea
                    value={patientContext}
                    onChange={(e) => setPatientContext(e.target.value)}
                    placeholder="Ex: Homme 68 ans, diabétique type 2, IRC modérée, sous metformine..."
                    className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-200 text-sm placeholder:text-slate-500 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all resize-none"
                    rows={2}
                  />
                </div>
                
                {/* Generate Recommendations Button */}
                <Button
                  onClick={fetchTreatmentRecommendation}
                  disabled={loadingRecommendation}
                  className="w-full py-3 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500"
                >
                  {loadingRecommendation ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Génération des recommandations...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5 mr-2" />
                      Obtenir un plan de traitement IA
                    </>
                  )}
                </Button>
                
                {/* Treatment Recommendations Display */}
                {treatmentRecommendation && (
                  <div className="space-y-4 animate-fade-in">
                    {/* Risk Summary */}
                    {treatmentRecommendation.risque_global && (
                      <div className={`p-4 rounded-xl border-2 ${
                        treatmentRecommendation.risque_global === 'très élevé' || treatmentRecommendation.risque_global === 'élevé'
                          ? 'bg-red-900/30 border-red-500'
                          : treatmentRecommendation.risque_global === 'modéré'
                          ? 'bg-orange-900/30 border-orange-500'
                          : 'bg-green-900/30 border-green-500'
                      }`}>
                        <div className="flex items-center gap-2 mb-2">
                          <Target className="w-5 h-5" />
                          <span className="font-bold uppercase">Risque {treatmentRecommendation.risque_global}</span>
                        </div>
                        {treatmentRecommendation.resume && (
                          <p className="text-sm text-slate-200">{treatmentRecommendation.resume}</p>
                        )}
                      </div>
                    )}
                    
                    {/* Therapeutic Objectives */}
                    {treatmentRecommendation.objectifs_therapeutiques?.length > 0 && (
                      <div className="p-4 bg-blue-900/20 border border-blue-500/30 rounded-xl">
                        <h4 className="font-semibold text-blue-300 flex items-center gap-2 mb-3">
                          <Target className="w-4 h-4" />
                          Objectifs thérapeutiques
                        </h4>
                        <ul className="space-y-1">
                          {treatmentRecommendation.objectifs_therapeutiques.map((obj, idx) => (
                            <li key={typeof obj === 'string' ? obj : `obj-${idx}`} className="text-sm text-slate-200 flex items-start gap-2">
                              <Check className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                              {obj}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {/* Non-Medication Measures */}
                    {treatmentRecommendation.mesures_non_medicamenteuses?.length > 0 && (
                      <div className="p-4 bg-emerald-900/20 border border-emerald-500/30 rounded-xl">
                        <h4 className="font-semibold text-emerald-300 flex items-center gap-2 mb-3">
                          <ClipboardCheck className="w-4 h-4" />
                          Mesures hygiéno-diététiques
                        </h4>
                        <div className="space-y-2">
                          {treatmentRecommendation.mesures_non_medicamenteuses.map((mesure, idx) => (
                            <div key={mesure.titre || `mesure-${idx}`} className="text-sm">
                              <span className="font-medium text-emerald-200">{mesure.titre}</span>
                              {mesure.details && (
                                <p className="text-slate-300 ml-4">→ {mesure.details}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Medication Treatment */}
                    {treatmentRecommendation.traitement_medicamenteux?.length > 0 && (
                      <div className="p-4 bg-purple-900/20 border border-purple-500/30 rounded-xl">
                        <h4 className="font-semibold text-purple-300 flex items-center gap-2 mb-3">
                          <Pill className="w-4 h-4" />
                          Traitement médicamenteux
                        </h4>
                        <div className="space-y-3">
                          {treatmentRecommendation.traitement_medicamenteux.map((trt, idx) => (
                            <div key={trt.classe || `trt-${idx}`} className="p-3 bg-slate-800/50 rounded-lg">
                              <p className="font-medium text-purple-200">{trt.classe}</p>
                              <p className="text-sm text-slate-300">
                                <span className="text-indigo-400">Molécules:</span> {trt.molecules}
                              </p>
                              {trt.posologie && (
                                <p className="text-sm text-slate-300">
                                  <span className="text-indigo-400">Posologie:</span> {trt.posologie}
                                </p>
                              )}
                              {trt.surveillance && (
                                <p className="text-sm text-slate-400">
                                  <span className="text-amber-400">Surveillance:</span> {trt.surveillance}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Monitoring */}
                    {treatmentRecommendation.surveillance?.length > 0 && (
                      <div className="p-4 bg-amber-900/20 border border-amber-500/30 rounded-xl">
                        <h4 className="font-semibold text-amber-300 flex items-center gap-2 mb-3">
                          <Activity className="w-4 h-4" />
                          Surveillance
                        </h4>
                        <div className="space-y-1">
                          {treatmentRecommendation.surveillance.map((surv, idx) => (
                            <p key={surv.parametre || `surv-${idx}`} className="text-sm text-slate-200">
                              • <span className="font-medium">{surv.parametre}</span>
                              {surv.frequence && <span className="text-slate-400"> - {surv.frequence}</span>}
                            </p>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* When to Refer */}
                    {treatmentRecommendation.quand_referer?.length > 0 && (
                      <div className="p-4 bg-red-900/20 border border-red-500/30 rounded-xl">
                        <h4 className="font-semibold text-red-300 flex items-center gap-2 mb-3">
                          <AlertTriangle className="w-4 h-4" />
                          Quand référer au spécialiste
                        </h4>
                        <ul className="space-y-1">
                          {treatmentRecommendation.quand_referer.map((ref, idx) => (
                            <li key={typeof ref === 'string' ? ref : `ref-${idx}`} className="text-sm text-slate-200 flex items-start gap-2">
                              <span className="text-red-400">•</span> {ref}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {/* Sources */}
                    {treatmentRecommendation.sources?.length > 0 && (
                      <p className="text-xs text-slate-500 text-center">
                        Sources: {treatmentRecommendation.sources.join(', ')}
                      </p>
                    )}
                    
                    {/* Chat Toggle */}
                    <Button
                      onClick={() => setShowChat(!showChat)}
                      variant="outline"
                      className="w-full border-indigo-500/50 text-indigo-300 hover:bg-indigo-900/30"
                    >
                      <MessageCircle className="w-4 h-4 mr-2" />
                      {showChat ? 'Masquer le chat' : 'Adapter selon le profil patient'}
                    </Button>
                  </div>
                )}
                
                {/* Chat Interface */}
                {showChat && treatmentRecommendation && (
                  <div className="border border-indigo-500/30 rounded-xl overflow-hidden animate-fade-in">
                    <div className="bg-indigo-900/30 px-4 py-2 border-b border-indigo-500/30">
                      <h4 className="font-medium text-indigo-300 flex items-center gap-2">
                        <MessageCircle className="w-4 h-4" />
                        Adapter les recommandations
                      </h4>
                      <p className="text-xs text-slate-400">Précisez le profil patient pour personnaliser le traitement</p>
                    </div>
                    
                    {/* Chat Messages */}
                    <div className="max-h-64 overflow-y-auto p-4 space-y-3 bg-slate-900/50">
                      {chatMessages.length === 0 && (
                        <div className="text-center text-slate-500 text-sm py-4">
                          <p>Exemples de questions :</p>
                          <p className="text-xs mt-2">• "Patient insuffisant rénal, quelles adaptations ?"</p>
                          <p className="text-xs">• "Interactions avec son traitement par AVK ?"</p>
                          <p className="text-xs">• "Alternative si contre-indication aux statines ?"</p>
                        </div>
                      )}
                      {chatMessages.map((msg, idx) => (
                        <div
                          key={`${msg.role}-${idx}-${(msg.content || '').length}`}
                          className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                        >
                          <div className={`max-w-[85%] p-3 rounded-xl text-sm ${
                            msg.role === 'user'
                              ? 'bg-indigo-600 text-white'
                              : 'bg-slate-800 text-slate-200 border border-slate-700'
                          }`}>
                            <p className="whitespace-pre-wrap">{msg.content}</p>
                          </div>
                        </div>
                      ))}
                      {loadingChat && (
                        <div className="flex justify-start">
                          <div className="bg-slate-800 border border-slate-700 p-3 rounded-xl">
                            <Loader2 className="w-5 h-5 animate-spin text-indigo-400" />
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Chat Input */}
                    <div className="p-3 border-t border-slate-700 bg-slate-900/30">
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={chatInput}
                          onChange={(e) => setChatInput(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendChatMessage()}
                          placeholder="Posez une question sur le traitement..."
                          className="flex-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-slate-200 text-sm placeholder:text-slate-500 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                        />
                        <Button
                          onClick={sendChatMessage}
                          disabled={loadingChat || !chatInput.trim()}
                          size="sm"
                          className="bg-indigo-600 hover:bg-indigo-500"
                        >
                          <Send className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* CSS for animations */}
      <style>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default ScoresPage;
